import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { generatePopupContentForMarker, initializeCollapsiblePools } from './mapLayoutService.js';
import './MarkerInfoPanel.css';
import {
    Typography, Row, Col, Button, Divider, Collapse, Input, Tabs,
    Select, Radio, Spin, Empty, List, Switch
} from 'antd';
const MarkerInfoPanel = ({
    markerData,
    onClose, 
          
    lootDataByGrade,
    ensureLootDataIsLoaded,
    isLoadingLoot,
}) => {
    const [markerSpecificLootData, setMarkerSpecificLootData] = useState(null);
    const [isFetchingMarkerLoot, setIsFetchingMarkerLoot] = useState(false);
    const [markerLootError, setMarkerLootError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [structuredMarkerContent, setStructuredMarkerContent] = useState(null);
    const [activeTabKey, setActiveTabKey] = useState(null);

    const activeTabContentRef = useRef(null); 
    const panelContainerRef = useRef(null);

    useEffect(() => {
        setMarkerSpecificLootData(null);
        setMarkerLootError(null);
        setIsFetchingMarkerLoot(false);
        setStructuredMarkerContent(null);
        setActiveTabKey(null);


        if (markerData && markerData.dungeonGradeKey) {
            const gradeKey = markerData.dungeonGradeKey;
            if (lootDataByGrade && lootDataByGrade.has(gradeKey)) {
                const existingLoot = lootDataByGrade.get(gradeKey);
                if (existingLoot) {
                    setMarkerSpecificLootData(existingLoot);
                    return;
                }
            }
            if (ensureLootDataIsLoaded) {
                setIsFetchingMarkerLoot(true);
                ensureLootDataIsLoaded(gradeKey)
                    .then((data) => {
                        setMarkerSpecificLootData(data || {});
                        setIsFetchingMarkerLoot(false);
                    })
                    .catch(err => {
                        console.error(`Error fetching loot for grade ${gradeKey}:`, err);
                        setMarkerLootError(`Loot details for grade ${gradeKey} currently unavailable.`);
                        setMarkerSpecificLootData({});
                        setIsFetchingMarkerLoot(false);
                    });
            }
        }
    }, [markerData, lootDataByGrade, ensureLootDataIsLoaded]);

    useEffect(() => {
        if (!markerData) {
            setStructuredMarkerContent(null);
            setActiveTabKey(null);
            return;
        }

        if (markerLootError || isFetchingMarkerLoot || (isLoadingLoot && !markerSpecificLootData)) {
            setStructuredMarkerContent(null); // Ensure loading/error state clears previous content
            setActiveTabKey(null);
            return;
        }
        
        if (typeof markerSpecificLootData === 'object') {
            const content = generatePopupContentForMarker(
                markerData.object_name,
                markerSpecificLootData,
                markerData.zIndexValue,
                markerData.moduleName,
                markerData.spawn_chances,
                markerData.gameMode,
                markerData.entityGroupId,
                markerData.dungeonGradeKey,
                markerData.associated_lootdrop_ids,
                markerData.LocalizedString || markerData.object_name,
                searchTerm,
                markerData.moduleDisplayName
            );
            setStructuredMarkerContent(content);

            if (content?.lootData?.tabs?.length > 0) {
                const currentActiveTabExists = content.lootData.tabs.some(tab => tab.key === activeTabKey);
                if (!currentActiveTabExists || !activeTabKey) {
                    setActiveTabKey(content.lootData.tabs[0].key);
                }
            } else {
                setActiveTabKey(null);
            }
        }

    }, [markerData, markerSpecificLootData, isLoadingLoot, isFetchingMarkerLoot, markerLootError, searchTerm, activeTabKey]);

    useEffect(() => {
        if (activeTabContentRef.current) {
            initializeCollapsiblePools(activeTabContentRef.current);
        }
        if (panelContainerRef.current) {
            panelContainerRef.current.scrollTop = 0;
        }
    }, [activeTabKey, structuredMarkerContent]);

    const handleTabClick = (tabKey) => {
        setActiveTabKey(tabKey);
    };

    const renderPanelContent = () => {
    if (!markerData) return null;

    if (markerLootError) return <div className="panel-error-message" dangerouslySetInnerHTML={{ __html: markerLootError }} />;
    
    const showLoading = isFetchingMarkerLoot || (isLoadingLoot && !markerSpecificLootData && !structuredMarkerContent);
    if (showLoading) {
        return <div className="panel-loading-message"><i>Loading loot details for grade {markerData.dungeonGradeKey}...</i></div>;
    }

    if (!structuredMarkerContent) {
        return <div className="panel-info-message"><i>Preparing information...</i></div>;
    }

    const { mainHeaderHtml, spawnChancesHtml, separatorHtml, lootData } = structuredMarkerContent;
    const activeTabData = lootData?.tabs?.find(tab => tab.key === activeTabKey);

    return (
        <>
            {mainHeaderHtml && <div dangerouslySetInnerHTML={{ __html: mainHeaderHtml }} />}
            {spawnChancesHtml && <div dangerouslySetInnerHTML={{ __html: spawnChancesHtml }} />}
            {(spawnChancesHtml && lootData && (lootData.tabs?.length > 0 || lootData.noLootMessageHtmlIfAny)) && separatorHtml &&
                <div dangerouslySetInnerHTML={{ __html: separatorHtml }} />
            }

            {lootData && (
                <div className="loot-section-container">
                    {lootData.overallLootTitleHtml && <div dangerouslySetInnerHTML={{ __html: lootData.overallLootTitleHtml}} />}
                    
                    {lootData.tabs && lootData.tabs.length > 0 && (
                        <div className="panel-tabs-container">
                            <div className="panel-tabs-nav my-scrollbar">
                                {lootData.tabs.map(tab => (
                                    <Button
                                        key={tab.key}
                                        type={tab.key === activeTabKey ? "primary" : "default"}
                                        onClick={() => handleTabClick(tab.key)}
                                        style={{flex:"1 0 0" }}
                                    >
                                        {tab.tabLabel.replace(/^id lootdrop drop\s*/i, '').replace(/^id lootdrop quest*/i, '').replace(/^id lootdrop monster*/i, '').replace(/^id lootdrop spawn*/i, '')}
                                    </Button>
                                ))}
                            </div>

                            {activeTabData && activeTabData.categorizedPools && (
                                <div className="panel-tab-content">
                                    <div ref={activeTabContentRef}> {/* Ref is on the wrapper of categories */}
                                        {activeTabData.categorizedPools.map((categoryGroup, index) => (
                                            <div key={`${activeTabData.key}-cat-${index}`} className="loot-category-group">
                                                {activeTabData.poolCount > 1 && index === 0 ? (
                                                    <h5 className="loot-category-header loot-category-header-with-controls">
                                                        <span>{categoryGroup.categoryLabel}</span>
                                                        <div className="panel-page-controls">
                                                            <a href="#" className="popup-expand-all-pools"
                                                               onClick={(e) => { e.preventDefault(); activeTabContentRef.current?.querySelectorAll('.collapsible-pool .pool-content').forEach(c => {c.style.display='block'; const i=c.previousElementSibling?.querySelector('.pool-toggle-icon'); if(i) i.textContent='▼';}); }}>
                                                                Expand All
                                                            </a>
                                                            <a href="#" className="popup-collapse-all-pools" style={{ marginLeft: '10px' }}
                                                               onClick={(e) => { e.preventDefault(); activeTabContentRef.current?.querySelectorAll('.collapsible-pool .pool-content').forEach(c => {c.style.display='none'; const i=c.previousElementSibling?.querySelector('.pool-toggle-icon'); if(i) i.textContent='►';}); }}>
                                                                Collapse All
                                                            </a>
                                                        </div>
                                                    </h5>
                                                ) : (
                                                    <h5 className="loot-category-header">{categoryGroup.categoryLabel}</h5>
                                                )}
                                                <div dangerouslySetInnerHTML={{ __html: categoryGroup.poolsHtmlForCategory }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {(!lootData.tabs || lootData.tabs.length === 0) && lootData.noLootMessageHtmlIfAny &&
                        <div dangerouslySetInnerHTML={{ __html: lootData.noLootMessageHtmlIfAny }} />
                    }
                </div>
            )}
            {!lootData && !isFetchingMarkerLoot && !isLoadingLoot && markerData && !markerLootError &&
                <div className="panel-info-message"><i>No loot information to display for this item.</i></div>
            }
        </>
    );
};

    return (
        <div ref={panelContainerRef} className="marker-info-panel my-scrollbar">
            <button type="button" className="marker-info-panel-close" onClick={onClose} title="Close Panel" aria-label="Close Panel">
                ×
            </button>

            <div className="marker-info-panel-search-container">
                <div className="marker-info-panel-search-input-wrapper">
                    <input
                        type="text"
                        placeholder="Search loot items..."
                        className="marker-info-panel-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search loot items"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="marker-info-panel-search-clear-button"
                            onClick={() => setSearchTerm('')}
                            aria-label="Clear search"
                            title="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>
            <div className="marker-info-panel-content">
                {renderPanelContent()}
            </div>
        </div>
    );
};

MarkerInfoPanel.propTypes = {
    markerData: PropTypes.shape({
        object_name: PropTypes.string.isRequired,
        LocalizedString: PropTypes.string,
        zIndexValue: PropTypes.number,
        moduleName: PropTypes.string.isRequired,
        moduleDisplayName: PropTypes.string,
        spawn_chances: PropTypes.object,
        gameMode: PropTypes.string.isRequired,
        entityGroupId: PropTypes.string,
        dungeonGradeKey: PropTypes.string.isRequired,
        associated_lootdrop_ids: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
    onClose: PropTypes.func.isRequired,
    lootDataByGrade: PropTypes.instanceOf(Map),
    ensureLootDataIsLoaded: PropTypes.func.isRequired,
    isLoadingLoot: PropTypes.bool,
};

MarkerInfoPanel.defaultProps = {
    markerData: null,
    lootDataByGrade: new Map(),
    isLoadingLoot: false, 
};

export default MarkerInfoPanel;